/**
 * Detects browser-global errors that must not crash the React tree.
 *
 * ResizeObserver defers notifications when a callback mutates layout in the same
 * frame. Browsers surface that as a window `error` event with a fixed message and
 * often `event.error === null` — see MDN ResizeObserver observation errors.
 */

/** Message fragment shared by Chromium and WebKit ResizeObserver loop notifications. */
const RESIZE_OBSERVER_LOOP_MESSAGE_FRAGMENT = "resizeobserver loop";

/**
 * Returns whether a window `error` event is a benign browser notification.
 *
 * @param event Global error event from `window.addEventListener("error", …)`.
 * @returns True when the event should be ignored by fatal error boundaries.
 */
export const isBenignWindowErrorEvent = ( event: ErrorEvent ): boolean => {
    const message = ( event.message ?? "" ).trim();
    const normalizedMessage = message.toLowerCase();

    if ( normalizedMessage.includes( RESIZE_OBSERVER_LOOP_MESSAGE_FRAGMENT ) ) {
        return true;
    }

    // Cross-origin script errors carry no details; do not replace the UI for them.
    if ( normalizedMessage === "script error." || normalizedMessage === "script error" ) {
        return true;
    }

    /*
     * Firefox iOS / WebKit sometimes emit `error` events with `event.error === null`
     * and an empty message during layout or ResizeObserver deferral.
     */
    if ( event.error == null && message === "" ) {
        return true;
    }

    return false;
};

/**
 * Returns whether a global error carries a value worth surfacing in the fallback UI.
 *
 * @param error Value from `ErrorEvent.error`.
 * @returns True when the boundary should treat the event as a real failure.
 */
export const isActionableGlobalErrorValue = ( error: unknown ): error is Error | string => {
    return error instanceof Error || typeof error === "string";
};
